import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, fileUrl } from "../api";
import { BrutalButton } from "./Brutal";
import { Eye, Storefront, X, Crown, Gear, PencilSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

// v414.96 — same split as core.helpers.WELLNESS_CATEGORIES / the store's
// wellness door: these rows price off the wellness markup, not the margin.
const WELLNESS_CATS = ["Mushroom Coffee", "Adaptogens", "Protein", "Supplements"];

// v414.98 — thin-margin alert: the sub's wholesale has met or passed the
// suggested retail, leaving them no room to sell. Fix = pin a lower margin.
const overRetail = (p) => p.ws != null && p.srp != null && Number(p.ws) >= Number(p.srp);

/** v409.10 — God Mode: see EXACTLY what this distributor sees.
 *  - "What they see" opens a dialog with the supplier catalog priced at THEIR
 *    wholesale (base + margin; hidden while pricing is pending).
 *  - "Their public store" opens /store/{code} — the live customer menu.
 *  v414.83 — per-product margin editor: pin the margin this account pays on
 *  ONE product (e.g. account +$5 but bac water pinned to +$1).
 *  v414.97 — `limited` = non-elevated DIRECT PARENT view: same price table +
 *  pin editor for their sub, without the god-mode store management button. */
export function GodModePreview({ distributor, limited = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const code = distributor.distributor_code;
  const firstName = (distributor.name || "").split(" ")[0] || "them";

  const fetchPreview = async () => {
    const { data } = await api.get(`/distributors/${distributor.id}/preview`);
    setPreview(data);
  };

  const openPreview = async () => {
    setOpen(true);
    if (preview) return;
    setLoading(true);
    try {
      await fetchPreview();
    } catch (e) {
      toast.error(formatApiError(e));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const d = preview?.distributor;
  const catalog = preview?.catalog || [];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <div
        className="border-2 border-[#c9a24a] bg-[#fbf6e7]/70 rounded-xl p-4 flex flex-wrap items-center gap-3"
        data-testid="god-mode-preview-card"
      >
        <Crown size={20} weight="fill" className="text-[#8a6a1c] shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <p className="font-semibold text-sm text-[#8a6a1c]">{limited ? `Price check — view as ${firstName}` : `God Mode — view as ${firstName}`}</p>
          <p className="text-xs text-zinc-600">Verify the exact pricing and store this account sees.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BrutalButton variant="elite" size="sm" onClick={openPreview} data-testid="view-as-distributor-btn">
            <Eye size={15} weight="bold" /> What {firstName} sees
          </BrutalButton>
          {code && (
            <BrutalButton
              variant="eliteFilled"
              size="sm"
              onClick={() => window.open(`/store/${code}`, "_blank", "noopener")}
              data-testid="open-public-store-btn"
            >
              <Storefront size={15} weight="bold" /> Their public store
            </BrutalButton>
          )}
          {code && !limited && (
            <BrutalButton
              variant="elite"
              size="sm"
              onClick={() => navigate(`/my-store?as=${distributor.id}`)}
              data-testid="manage-their-store-btn"
            >
              <Gear size={15} weight="bold" /> Manage their store
            </BrutalButton>
          )}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white border-2 border-[#c9a24a] rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="distributor-preview-dialog"
          >
            <div className="p-5 border-b border-zinc-100 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Crown size={18} weight="fill" className="text-[#c9a24a]" />
                  Catalog as {distributor.name} sees it
                </h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  {d?.pricing_pending ? (
                    <span className="text-[11px] font-semibold px-2 py-0.5 bg-amber-50 text-amber-900 border border-amber-300 rounded-full" data-testid="preview-pricing-pending">
                      Pricing pending — they see NO wholesale prices
                    </span>
                    ) : (
                    <>
                    <span className="text-[11px] font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-full" data-testid="preview-margin-chip">
                      {(d?.margin_type || "flat") === "percent"
                        ? `Margin +${Number(d?.margin_surcharge || 0)}% of base`
                        : `Margin +$${Number(d?.margin_surcharge || 0).toFixed(2)}/unit`}
                    </span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 bg-teal-50 text-teal-800 border border-teal-300 rounded-full" data-testid="preview-wellness-chip">
                      {d?.wellness_margin_surcharge != null
                        ? ((d?.wellness_margin_type || "flat") === "percent"
                          ? `Wellness +${Number(d.wellness_margin_surcharge)}% of base`
                          : `Wellness +$${Number(d.wellness_margin_surcharge).toFixed(2)}/unit`)
                        : "Wellness: base price"}
                    </span>
                    </>
                  )}
                  {d?.store_discount_pct > 0 && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 bg-violet-50 text-violet-800 border border-violet-300 rounded-full" data-testid="preview-store-discount">
                      Storewide discount {d.store_discount_pct}%
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900" data-testid="preview-close-btn">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              {loading ? (
                <p className="text-sm font-medium text-zinc-500">Loading…</p>
              ) : catalog.length === 0 ? (
                <p className="text-sm font-medium text-zinc-500" data-testid="preview-empty">
                  No supplier catalog for this account.
                </p>
              ) : (
                <>
                {catalog.some(overRetail) && (
                  <div className="mb-3 flex items-start gap-2 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2" data-testid="thin-margin-banner">
                    <span className="text-red-600 font-bold text-sm leading-tight">⚠</span>
                    <p className="text-[12px] font-medium text-red-800">
                      {catalog.filter(overRetail).length} product{catalog.filter(overRetail).length !== 1 ? "s" : ""} priced at or above suggested retail — {firstName} can't make money selling {catalog.filter(overRetail).length !== 1 ? "them" : "it"} at retail. Pin a lower margin with the pencil on those rows.
                    </p>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                      <th className="py-2 pr-2">Product</th>
                      <th className="py-2 pr-2 text-right">Their WS</th>
                      <th className="py-2 pr-2 text-right">SRP</th>
                      <th className="py-2 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.map((p) => (
                      <tr key={p.id} className="border-b border-zinc-100" data-testid={`preview-row-${p.id}`}>
                        <td className="py-2 pr-2">
                          <div className="flex items-center gap-2">
                            {p.photo_path && (
                              <img src={fileUrl(p.photo_path)} alt="" className="w-7 h-7 rounded object-cover border border-zinc-200" />
                            )}
                            <div>
                              <p className="font-medium text-zinc-900">{p.name}</p>
                              {p.category && <p className="text-[11px] text-zinc-400">{p.category}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <MarginCell
                            row={p}
                            dist={d}
                            distributorId={distributor.id}
                            onSaved={fetchPreview}
                          />
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums text-zinc-600">{money(p.srp)}</td>
                        <td className="py-2 text-right">
                          {p.already_held ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-full">Held</span>
                          ) : p.made_to_order ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-300 rounded-full" data-testid={`preview-mto-${p.id}`}>Made to order</span>
                          ) : p.available ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">Available</span>
                          ) : (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full">Out</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** v414.83 — "Their WS" cell with the per-product margin editor. Shows the
 *  final price + the margin under it; pencil pins a custom margin for THIS
 *  product only (overriding the account-level surcharge), reset restores. */
function MarginCell({ row, dist, distributorId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const key = (row.name || "").trim().toLowerCase();
  const override = (dist?.margin_overrides || {})[key];
  const isWellness = WELLNESS_CATS.includes(row.category);
  // margin_eff is computed server-side (v414.85) so the editor also works on
  // org-fallback rows where the sub-facing ws is hidden.
  const hasBase = row.base_ws != null && row.margin_eff != null;
  const margin = hasBase ? Number(row.margin_eff) : null;

  const save = async (amount) => {
    setBusy(true);
    try {
      await api.put(`/distributors/${distributorId}/margin-override`, { name: row.name, amount });
      toast.success(amount == null ? "Override removed" : `Margin pinned at $${Number(amount).toFixed(2)}`);
      setEditing(false);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center justify-end gap-1" data-testid={`margin-editor-${row.id}`}>
        <span className="text-[11px] text-zinc-500">+$</span>
        <input
          type="number"
          min="0"
          step="0.25"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="w-16 h-7 px-1.5 border border-[#c9a24a] rounded text-right text-sm tabular-nums focus:outline-none"
          autoFocus
          data-testid={`margin-input-${row.id}`}
        />
        <button
          type="button"
          disabled={busy || val === ""}
          onClick={() => save(Number(val))}
          className="h-7 px-2 rounded bg-zinc-950 text-[#c9a24a] text-[11px] font-bold disabled:opacity-40"
          data-testid={`margin-save-${row.id}`}
        >
          Save
        </button>
        <button type="button" onClick={() => setEditing(false)} className="h-7 px-1.5 text-zinc-400 hover:text-zinc-800">
          <X size={13} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1">
        <span className={`font-semibold tabular-nums ${overRetail(row) ? "text-red-600" : ""}`} data-testid={`preview-ws-${row.id}`}>{money(row.ws)}</span>
        {hasBase && (
          <button
            type="button"
            title="Pin a custom margin for this product"
            onClick={() => { setVal(margin != null ? String(Number(margin.toFixed(2))) : ""); setEditing(true); }}
            className="w-6 h-6 rounded flex items-center justify-center text-zinc-300 hover:text-[#8a6a1c] hover:bg-[#fbf6e7]"
            data-testid={`margin-edit-${row.id}`}
          >
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </div>
      {hasBase && margin != null && (
        <div className="flex items-center gap-1">
          <span
            className={`text-[10px] font-semibold ${override != null ? "text-[#8a6a1c]" : isWellness ? "text-teal-600" : "text-zinc-400"}`}
            data-testid={`margin-chip-${row.id}`}
          >
            +${margin.toFixed(2)}{override != null ? " pinned" : isWellness ? " wellness" : " margin"}
          </span>
          {override != null && (
            <button
              type="button"
              title="Remove override — back to the account margin"
              disabled={busy}
              onClick={() => save(null)}
              className="text-zinc-300 hover:text-zinc-700"
              data-testid={`margin-reset-${row.id}`}
            >
              <ArrowCounterClockwise size={11} weight="bold" />
            </button>
          )}
        </div>
      )}
      {overRetail(row) && (
        <span className="text-[10px] font-bold text-red-600" data-testid={`over-retail-${row.id}`}>
          {Number(row.ws) > Number(row.srp)
            ? `▲ $${(Number(row.ws) - Number(row.srp)).toFixed(2)} over retail`
            : "▲ equals retail — no room"}
        </span>
      )}
    </div>
  );
}
